<template>
    <TabGroup
        v-if="!isMobile"
        v-model="activeTab"
        :tabs="betsStore.tabs"
        :reset-on-route-change="false"
        variant="s"
        class="my-tickets__tabs"
    />
    <SazkaSpinLoader v-if="activeTabSource === 'store' && (!betsStore.initialized || fetchMoreInProgress)" center />
    <div class="my-tickets__list" ref="myTicketsListRef" v-if="bets?.length">
        <MyTicket
            v-for="(bet, index) in bets"
            :key="bet.id"
            v-bind="bet"
            v-element-visibility="(isVisible) => onElementVisibility(isVisible, index)"
        />
        <div
            v-if="activeTabSource === 'local' && isMoreBetsAvailable && fetchMoreInProgress"
            class="my-tickets__skeleton-loader"
        >
            <Skeleton :height="isMobile ? '133px' : '80px'" width="100%" border-radius="8px" />
            <div class="my-tickets__skeleton-loader__inner">
                <SazkaSpinLoader />
            </div>
        </div>
    </div>
    <div class="my-tickets__empty" v-else-if="betsStore.initialized">
        <SvgIcon name="tickets" :size="64" />
        {{ emptyText }}
    </div>
</template>

<script lang="ts" setup>
import { computed, ComputedRef, Ref, ref, watch } from "vue";
import MyTicket from "@/my-tickets/components/molecules/MyTicketLink.vue";
import TabGroup from "@/shared/components/organisms/TabGroup.vue";
import useIsBreakpoint from "@/shared/composables/use-is-breakpoint";
import { useBetsStore } from "@/shared/stores/use-bets-store";
import { BetData, BetType } from "@/shared/model/bet";
import SvgIcon from "@/shared/components/atoms/SvgIcon.vue";
import SazkaSpinLoader from "@/shared/components/atoms/SazkaSpinLoader.vue";
import Skeleton from "@/shared/components/atoms/Skeleton.vue";
import { vElementVisibility } from "@vueuse/components";
import analytics from "@/shared/analytics";

const props = withDefaults(
    defineProps<{
        activeTabSource?: "store" | "local";
    }>(),
    {
        activeTabSource: "store",
    },
);

const myTicketsListRef = ref<HTMLDivElement>();

const betsStore = useBetsStore();

const { isMobile } = useIsBreakpoint();

const activeTabLocal: Ref<BetType> = ref(BetType.OPEN);
const fetchMoreInProgress: Ref<boolean> = ref(false);
const activeTab = computed({
    get: () => (props.activeTabSource === "store" ? betsStore.activeTab : activeTabLocal.value),
    set: (val) => (props.activeTabSource === "store" ? (betsStore.activeTab = val) : (activeTabLocal.value = val)),
});

const bets: ComputedRef<BetData[]> = computed(() => {
    let b: BetData[] = [];
    switch (activeTab.value) {
        case BetType.OPEN:
            b = betsStore.openBets;
            break;
        case BetType.SETTLED:
            b = betsStore.settledBets;
            break;
        case BetType.CASHOUT:
            b = betsStore.cashoutBets;
            break;
    }
    return b;
});

const emptyText = computed(() => {
    switch (activeTab.value) {
        case BetType.OPEN:
            return "Nemáte žádné otevřené tikety";
        case BetType.SETTLED:
            return "Nemáte žádné vyhodnocené tikety";
        case BetType.CASHOUT:
            return "Nemáte žádné cashout tikety";
        default:
            return "";
    }
});

const countText: ComputedRef<string> = computed(() => {
    let c: string;
    switch (activeTab.value) {
        case BetType.OPEN:
            c = betsStore.openBetsCountText;
            break;
        case BetType.SETTLED:
            c = betsStore.settledBetsCountText;
            break;
        case BetType.CASHOUT:
            c = betsStore.cashoutBetsCountText;
            break;
    }
    return c;
});

const isMoreBetsAvailable: ComputedRef<boolean> = computed(() => countText.value.split("")?.pop() === "+");

const onElementVisibility = (isVisible: boolean, index: number) => {
    if (isMoreBetsAvailable.value && index + 1 === Number(countText.value.slice(0, -1)) && isVisible) {
        fetchMoreInProgress.value = true;
        betsStore.fetchMoreBets(activeTab.value).finally(() => {
            fetchMoreInProgress.value = false;
        });
    }
};

watch(activeTab, (newValue) => {
    (isMobile.value ? window : myTicketsListRef.value)?.scrollTo({ top: 0, left: 0, behavior: "instant" });

    const analyticsLabelMap = {
        [BetType.OPEN]: "otevrene",
        [BetType.SETTLED]: "vyhodnocene",
        [BetType.CASHOUT]: "cashout",
    };

    const analyticsLabel = analyticsLabelMap[newValue] || "";

    analytics.sendPageCategoryNavigation(analyticsLabel, "kurzovesazky.tickets");
});
</script>
